import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useMenuAuthInfo } from "../../services/GlobalUtil";
import api from "../../services/api";
import moment from "moment";
import BreadCrumbDiv from "../../components/common/BreadCrumbDiv";
import SearchWrapDiv from "../../components/common/SearchWrapDiv";
import SearchInput from "../../components/common/SearchInput";
import SearchButton from "../../components/common/SearchButton";
import FunctionButton from "../../components/common/FunctionButton";
import ButtonWrapDiv from "../../components/common/ButtonWrapDiv";
import LoadingBoxDiv from "../../components/common/LoadingBoxDiv";
import ListWrapDiv from "../../components/common/ListWrapDiv";
import PageBar from "../../components/common/PageBar";
import listIcon from "../../assets/images/ico_list.png";

const Badge = styled.span`
  display: inline-block;
  margin-left: 4px;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: bold;
  line-height: 16px;
  vertical-align: middle;
  color: #fff;
  background-color: ${(props) => props.$color};
`;

/** 등록일시가 당일 00:00 이후인지 여부 */
const isNewNotice = (creDtm) =>
  Boolean(creDtm) && moment(creDtm).isSameOrAfter(moment().startOf("day"));

const MainNoticePage = () => {
  const { t } = useTranslation();
  const menuAuthInfo = useMenuAuthInfo();
  const [curPage, setCurPage] = useState(1);
  const [totalCount, setTotalCount] = useState();
  const pageSize = 12;
  const [list, setList] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const navigate = useNavigate();

  const loadContentData = () => {
    api
      .post("/notice/main/list", {
        page: curPage,
        size: pageSize,
        keyword: keyword,
      })
      .then((response) => {
        setList(response.data.content);
        setTotalCount(response.data.totalCount);
      })
      .catch((error) => {
        console.error("Error loading content data:", error);
      });
  };

  useEffect(() => {
    if (menuAuthInfo.view) {
      loadContentData();
    }
  }, [curPage]);

  const pageChange = (data) => {
    if (curPage === data) {
      return;
    }
    setCurPage(data);
  };

  const changeKeyword = (e) => {
    setKeyword(e.target.value);
  };

  const handleSearchClick = () => {
    setCurPage(1);
    loadContentData();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchClick();
    }
  };

  const handleAddNotice = () => {
    navigate("/notice/main/view");
  };

  const handleCheckboxChange = (e) => {
    const id = e.target.value;
    if (e.target.checked) {
      setSelectedIds((prev) => [...prev, id]);
    } else {
      setSelectedIds((prev) => prev.filter((item) => item !== id));
    }
  };

  const handleDeleteSelected = () => {
    if (selectedIds.length === 0) {
      alert("Please select at least one item to delete.");
      return;
    }

    api
      .post("/notice/main/delete", selectedIds)
      .then((response) => {
        if (response.data > 0) {
          setSelectedIds([]);
          loadContentData();
        }
      })
      .catch((error) => {
        console.error("Error deleting selected items:", error);
      });
  };

  return (
    <>
      <BreadCrumbDiv items={[t("MENU:L0055"), t("MENU:L0069")]} />

      <SearchWrapDiv>
        <SearchInput
          width={"495px"}
          value={keyword}
          onChange={changeKeyword}
          onKeyDown={handleKeyDown}
        />
        <SearchButton onClick={handleSearchClick} />
        <ButtonWrapDiv className={"btn_right"}>
          {menuAuthInfo.reg && (
            <FunctionButton
              value={t("L0399")}
              onClick={handleAddNotice}
            ></FunctionButton>
          )}
          {menuAuthInfo.del && (
            <FunctionButton
              value={t("L0091")}
              className={"bg_gray"}
              onClick={handleDeleteSelected}
            ></FunctionButton>
          )}
        </ButtonWrapDiv>
      </SearchWrapDiv>

      <LoadingBoxDiv>
        <ListWrapDiv>
          <table>
            <colgroup>
              <col style={{ width: "5%" }} />
              <col style={{ width: "6%" }} />
              <col style={{ width: "auto" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "5%" }} />
            </colgroup>
            <thead>
              <tr>
                <th></th>
                <th>{t("L0130")}</th>
                <th>{t("L0131")}</th>
                <th>{t("L0400")}</th>
                <th>{t("L0133")}</th>
                <th>{t("L0132")}</th>
                <th>{t("L0939")}</th>
              </tr>
              {list.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center" }}>
                    {t("L0094")}
                    <br />
                    {t("L0095")}
                  </td>
                </tr>
              ) : (
                list.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        type="checkbox"
                        name="post_ids"
                        id={`post_ids_${item.id}`}
                        value={item.id}
                        checked={selectedIds.includes(String(item.id))}
                        onChange={handleCheckboxChange}
                      />
                      <label htmlFor={`post_ids_${item.id}`}></label>
                    </td>
                    <td>{item.rownum}</td>
                    <td>
                      {item.title ? (
                        <>
                          <Link
                            to={`/notice/main/view/${item.id}`}
                            state={{ id: item.id }}
                          >
                            {item.title}
                          </Link>
                          {isNewNotice(item.creDtm) && (
                            <Badge $color="#2e86c1">{t("L1140", "NEW")}</Badge>
                          )}
                        </>
                      ) : (
                        <></>
                      )}
                    </td>
                    <td>{item.crerId}</td>
                    <td>{item.viewCnt}</td>
                    <td>{item.creDtm ? moment(item.creDtm).format("YYYY-MM-DD HH:mm:ss") : ""}</td>
                    <td>
                      {item.fileName1 != null ||
                      item.fileName2 != null ||
                      item.fileName3 != null ? (
                        <img src={listIcon}></img>
                      ) : (
                        <></>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </thead>
          </table>
        </ListWrapDiv>

        <PageBar
          TotalCount={totalCount}
          CurrentPage={curPage}
          CountPerPage={pageSize}
          MaxPageBarCount={10}
          onPageСhange={pageChange}
        ></PageBar>
      </LoadingBoxDiv>
    </>
  );
};

export default MainNoticePage;
